import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { AlarmRuleAddOrUpdateDto, AlarmRuleDto } from '../types/dtos/alarm-rule.dto';
import { BaseResponseDto } from '../types/dtos/base.dto';
import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root',
})
export class AlarmRuleService extends BaseService {
  private _url: string = `${ this._baseUrl }/alarm-rule`;

  private _alarmRuleSubject: BehaviorSubject<AlarmRuleDto[]> = new BehaviorSubject<AlarmRuleDto[]>([]);
  public alarmRule$: Observable<AlarmRuleDto[]> = this._alarmRuleSubject.asObservable();

  constructor(
    private readonly _httpClient: HttpClient,
  ) {
    super();

    if (this._alarmRuleSubject.value.length === 0) {
      this.fetchAlarmRules();
    }
  }

  public fetchAlarmRules(): void {
    this._httpClient.get<BaseResponseDto<AlarmRuleDto[]>>(`${ this._url }`)
      .subscribe({
        next: (result: BaseResponseDto<AlarmRuleDto[]>) => {
          let alarmRules: AlarmRuleDto[] = result.data ?? [];
          this._alarmRuleSubject.next(alarmRules);
        },
        error: (error) => console.error('Error fetching alarm rules:', error),
      });
  }

  public getAll(): Observable<BaseResponseDto<AlarmRuleDto[]>> {
    return this._httpClient.get<BaseResponseDto<AlarmRuleDto[]>>(this._url)
      .pipe(take(1));
  }

  public getById(id: number): Observable<BaseResponseDto<AlarmRuleDto>> {
    return this._httpClient.get<BaseResponseDto<AlarmRuleDto>>(`${ this._url }/${ id }`)
      .pipe(take(1));
  }

  public update(alarmRule: AlarmRuleAddOrUpdateDto): Observable<BaseResponseDto<AlarmRuleDto>> {
    return this._httpClient.put<BaseResponseDto<AlarmRuleDto>>(`${ this._url }/${ alarmRule.id }`, alarmRule)
      .pipe(take(1));
  }

  public add(alarmRule: AlarmRuleAddOrUpdateDto): Observable<BaseResponseDto<AlarmRuleDto>> {
    return this._httpClient.post<BaseResponseDto<AlarmRuleDto>>(this._url, alarmRule)
      .pipe(take(1));
  }

  public delete(id: number): Observable<void> {
    return this._httpClient.delete<void>(`${ this._url }/${ id }`)
      .pipe(take(1));
  }

  public isAlarmActive(alarmRule: AlarmRuleDto, deviceId?: number): boolean {
    if (!alarmRule.is_active) return false;

    if (deviceId && alarmRule.devices) {
      let deviceConfig = alarmRule.devices
        .find(device => device.device_id === deviceId);
      if (!deviceConfig) return true; // No device Override

      if (!deviceConfig.is_active) return false;
    }

    return true;
  }

  // function to create text when alarmRule is diable for notice why.
  public disabledAlarmNotice(alarmRule: AlarmRuleDto, deviceId?: number): string {
    if (!alarmRule.is_active) return 'common.status.rule-status.global-disabled';

    if (deviceId && alarmRule.devices) {
      let deviceConfig = alarmRule.devices
        .find(device => device.device_id === deviceId);
      if (!deviceConfig) return '-';

      if (!deviceConfig.is_active) return 'common.status.rule-status.device-disabled';
    }

    return '-'
  }
}
